import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Gift, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const AMOUNTS = [50, 75, 100, 150, 250];

export default function GiftCards() {
  const [searchParams] = useSearchParams();
  const wasCancelled = searchParams.get("cancelled") === "1";
  const [amount, setAmount] = useState(75);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "",
    recipient_name: "", recipient_email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 25) {
      return toast.error("Montant minimum : 25 €");
    }
    if (finalAmount > 2000) {
      return toast.error("Montant maximum : 2000 €");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", {
        amount: finalAmount,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="gift-cards-page">
      {wasCancelled && (
        <div className="rounded-xl border border-border bg-card p-4 mb-8 text-sm text-muted-foreground" data-testid="gc-cancel-notice">
          Paiement annulé. Aucun montant n'a été débité.
        </div>
      )}

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Cartes cadeaux</div>
          <h1 className="font-display text-5xl lg:text-6xl mb-5">Offrez une expérience.</h1>
          <p className="text-muted-foreground leading-relaxed mb-10 max-w-xl">
            Atelier rhum arrangé, soirée afterwork ou table au bar : la carte cadeau Saveurs Normandes ouvre les portes de toutes nos expériences. Idéal pour un anniversaire, un EVG ou simplement faire plaisir.
          </p>

          <ul className="space-y-4 mb-10">
            {[
              ["Envoi instantané", "Le destinataire reçoit sa carte et son PDF par email dès le paiement validé."],
              ["Valable 12 mois", "Utilisable en une ou plusieurs fois jusqu'à épuisement du solde."],
              ["Sur tous nos événements", "Afterworks, ateliers cocktails, ateliers rhum arrangé."],
            ].map(([title, text]) => (
              <li key={title} className="flex gap-4 items-start">
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-display text-lg">{title}</div>
                  <div className="text-sm text-muted-foreground">{text}</div>
                </div>
              </li>
            ))}
          </ul>

          {/* Visual gift card preview */}
          <div className="rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-card via-card to-primary/10 p-7 lg:p-9 relative overflow-hidden" data-testid="gc-preview">
            <div className="absolute top-0 right-0 w-40 h-40 -mr-12 -mt-12 rounded-full bg-primary/10 blur-3xl" />
            <Gift className="w-7 h-7 text-primary mb-4" />
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Carte Cadeau</div>
            <div className="font-display text-6xl gold-text mb-3">{finalAmount ? `${finalAmount.toFixed(0)} €` : "—"}</div>
            <div className="text-sm text-muted-foreground">SAVEURS NORMANDES · Cocktails · Events · Normandie</div>
            <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground font-mono tracking-widest">
              SN-XXXX-XXXX
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6 lg:p-8 lg:sticky lg:top-28" data-testid="gc-form">
          <h2 className="font-display text-3xl mb-5">Composer le cadeau</h2>

          <div className="mb-6">
            <Label className="mb-3 inline-block">Montant</Label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {AMOUNTS.map((a) => (
                <button
                  type="button"
                  key={a}
                  onClick={() => { setAmount(a); setCustomAmount(""); }}
                  className={`py-3 rounded-lg border text-sm font-medium transition-colors ${
                    !customAmount && amount === a
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-input text-foreground hover:border-primary/40"
                  }`}
                  data-testid={`gc-amount-${a}`}
                >
                  {a} €
                </button>
              ))}
            </div>
            <Input
              type="number"
              min={25}
              max={2000}
              step={5}
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="Ou un montant libre (25 € à 2000 €)"
              className="bg-input"
              data-testid="gc-custom-amount"
            />
          </div>

          <div className="space-y-4 pt-5 border-t border-border">
            <div className="text-xs uppercase tracking-[0.25em] text-primary">Vos coordonnées</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Votre nom *</Label>
                <Input value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} required className="mt-2 bg-input" data-testid="gc-buyer-name" />
              </div>
              <div>
                <Label>Votre email *</Label>
                <Input type="email" value={form.buyer_email} onChange={(e) => set("buyer_email", e.target.value)} required className="mt-2 bg-input" data-testid="gc-buyer-email" />
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-5 border-t border-border mt-5">
            <div className="text-xs uppercase tracking-[0.25em] text-primary">Pour qui ?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Prénom du destinataire *</Label>
                <Input value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} required className="mt-2 bg-input" data-testid="gc-recipient-name" />
              </div>
              <div>
                <Label>Email du destinataire *</Label>
                <Input type="email" value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} required className="mt-2 bg-input" data-testid="gc-recipient-email" />
              </div>
            </div>
            <div>
              <Label>Petit mot (optionnel)</Label>
              <Textarea
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Joyeux anniversaire ! Je t'offre une expérience à la maison…"
                maxLength={280}
                className="mt-2 bg-input min-h-[90px]"
                data-testid="gc-message"
              />
              <div className="text-[11px] text-muted-foreground mt-1 text-right">{form.message.length}/280</div>
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="w-full mt-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12" data-testid="gc-submit">
            <Sparkles className="w-4 h-4 mr-2" />
            {submitting ? "Redirection…" : `Payer ${finalAmount ? finalAmount.toFixed(2) : "0.00"} €`}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-[11px] text-muted-foreground text-center mt-3">
            Paiement sécurisé Stripe · Le destinataire reçoit la carte par email immédiatement
          </p>
        </form>
      </div>
    </div>
  );
}
